import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReportShell } from "@/features/reports/ReportShell";
import { apiList } from "@/integrations/api/client";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import type { ReportFilters } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/grn-register")({
  component: GrnRegister,
});

type Row = {
  id: string;
  grn_no: string;
  received_date: string;
  po_number: string | null;
  invoice_no: string | null;
  invoice_date: string | null;
  kind: string;
  branch: { id: string; name: string } | null;
  vendor: { name: string } | null;
  grn_items: Array<{
    quantity_ordered: number;
    quantity_received: number;
    accepted_quantity: number;
    rejected_quantity: number;
    good_quantity: number | null;
    package_damaged_quantity: number | null;
    defective_quantity: number | null;
    shortage_quantity: number | null;
    line_total: number;
    tax_amount: number;
    asset_creation_status: string | null;
    created_asset_count: number | null;
  }> | null;
};

function useGrnRegister(filters: ReportFilters, effectiveBranch: string | null) {
  return useQuery({
    queryKey: ["report-grn-register", filters, effectiveBranch],
    queryFn: async () => {
      const params: Record<string, any> = { limit: 1000 };
      if (effectiveBranch) params.branch = effectiveBranch;
      if (filters.vendorId) params.vendor = filters.vendorId;
      if (filters.dateFrom) params.received_date__gte = filters.dateFrom;
      if (filters.dateTo) params.received_date__lte = filters.dateTo;
      if (filters.search?.trim()) params.search = filters.search.trim();
      
      const res = await apiList<any>("/api/v1/procurement/grns/", params);
      // Map API response to the expected Row structure
      const rows = (res.results || []).map((grn: any) => ({
        id: grn.id,
        grn_no: grn.grn_number || grn.grn_no,
        received_date: grn.received_date,
        po_number: grn.po_number || grn.po,
        invoice_no: grn.invoice_number || grn.invoice_no,
        invoice_date: grn.invoice_date,
        kind: grn.po_type || "asset",
        branch: grn.branch ? { id: grn.branch, name: "Branch" } : null,
        vendor: grn.vendor ? { name: grn.vendor_name || "Vendor" } : null,
        grn_items: grn.items || []
      }));
      return rows as Row[];
    },
  });
}

function summarizeAssetStatus(items: Row["grn_items"]): string {
  if (!items?.length) return "—";
  const statuses = new Set(items.map((i) => i.asset_creation_status ?? "").filter(Boolean));
  if (statuses.size === 0) return "—";
  if (statuses.size === 1) return Array.from(statuses)[0];
  if (statuses.has("Pending") || statuses.has("Partially Created")) return "Partially Created";
  return "Mixed";
}

function GrnRegister() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const locked = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranch = locked ?? filters.branchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = useGrnRegister(filters, effectiveBranch);

  const shaped = useMemo(
    () =>
      rows.map((r) => {
        const items = r.grn_items ?? [];
        const isLegacy = items.length === 0;
        const ordered = items.reduce((s, i) => s + Number(i.quantity_ordered || 0), 0);
        const received = items.reduce((s, i) => s + Number(i.quantity_received || 0), 0);
        const accepted = items.reduce((s, i) => s + Number(i.accepted_quantity || 0), 0);
        const good = items.reduce((s, i) => s + Number(i.good_quantity || 0), 0);
        const pkg = items.reduce((s, i) => s + Number(i.package_damaged_quantity || 0), 0);
        const defective = items.reduce((s, i) => s + Number(i.defective_quantity || 0), 0);
        const shortage = items.reduce((s, i) => s + Number(i.shortage_quantity || 0), 0);
        const tax = items.reduce((s, i) => s + Number(i.tax_amount || 0), 0);
        const subtotal = items.reduce((s, i) => s + Number(i.line_total || 0) - Number(i.tax_amount || 0), 0);
        const total = subtotal + tax;
        const createdAssets = items.reduce((s, i) => s + Number(i.created_asset_count || 0), 0);
        return {
          id: r.id,
          grn_no: r.grn_no,
          date: r.received_date,
          po_number: r.po_number ?? "—",
          vendor: r.vendor?.name ?? "—",
          branch: r.branch?.name ?? "—",
          invoice_no: r.invoice_no ?? "—",
          invoice_date: r.invoice_date ?? "—",
          ordered,
          received,
          good,
          pkg,
          defective,
          shortage,
          accepted,
          subtotal,
          tax,
          total,
          asset_status: isLegacy ? "Legacy" : summarizeAssetStatus(items),
          created_assets: createdAssets,
          is_legacy: isLegacy,
          kind: r.kind,
        };
      }),
    [rows],
  );

  const exportRows = () =>
    shaped.map((r) => ({
      "GRN Number": r.grn_no,
      "GRN Date": r.date,
      "PO Number": r.po_number,
      Vendor: r.vendor,
      Branch: r.branch,
      "Invoice Number": r.invoice_no,
      "Invoice Date": r.invoice_date,
      "Ordered Qty": r.ordered,
      "Received Qty": r.received,
      "Good Condition Qty": r.good,
      "Package Damaged Qty": r.pkg,
      "Defective Qty": r.defective,
      "Shortage Qty": r.shortage,
      "Accepted Qty": r.accepted,
      Subtotal: r.subtotal.toFixed(2),
      "Tax Amount": r.tax.toFixed(2),
      "Grand Total": r.total.toFixed(2),
      "Asset Creation Status": r.asset_status,
      "Assets Created": r.created_assets,
      Kind: r.kind,
    }));

  return (
    <ReportShell
      id="R-43"
      title="GRN Register"
      description="Goods Receipt Notes with accepted/rejected quantities, invoice details and asset creation progress."
      filters={filters}
      onChange={setFilters}
      enabled={["branchId", "vendorId", "dateFrom", "dateTo", "search"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>GRN #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>PO #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Good</TableHead>
                  <TableHead className="text-right">Pkg Dmg</TableHead>
                  <TableHead className="text-right">Defective</TableHead>
                  <TableHead className="text-right">Shortage</TableHead>
                  <TableHead className="text-right">Accepted</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Asset Status</TableHead>
                  <TableHead className="text-right">Assets</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shaped.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">
                      <Link to="/grns/$grnId" params={{ grnId: r.id }} className="text-primary hover:underline">
                        {r.grn_no}
                      </Link>
                      {r.is_legacy && (
                        <Badge variant="outline" className="ml-1 text-[10px]">
                          Legacy
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell className="font-mono text-xs">{r.po_number}</TableCell>
                    <TableCell>{r.vendor}</TableCell>
                    <TableCell>{r.branch}</TableCell>
                    <TableCell className="text-xs">
                      {r.invoice_no}
                      {r.invoice_date !== "—" && <div className="text-muted-foreground">{r.invoice_date}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.ordered}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.received}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.good}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.pkg}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.defective}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.shortage}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.accepted}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{r.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{r.asset_status}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.created_assets}</TableCell>
                  </TableRow>
                ))}
                {shaped.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={16} className="py-8 text-center text-sm text-muted-foreground">
                      No GRNs match these filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">
            Showing {shaped.length} GRNs (first 1000). Legacy GRNs show a Legacy badge and zero aggregates.
          </div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
